import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Gift, CheckCircle2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm uppercase tracking-wider text-white outline-none focus:border-[#ec4899]/60";

export default function PinkPinActivate() {
  const { user } = useAuth();
  const { data: dash, reload } = useFetch("/user/dashboard");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const isActive = dash?.status === "active";

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter your Pink Pin code");
    setBusy(true);
    try {
      const { data } = await api.post("/user/pink-pin/activate", { code: code.trim() });
      setResult(data); setCode(""); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Activation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Activate with Pink Pin" subtitle="Enter a Pink Pin assigned to you for a FREE account activation — no deposit required." />

      <div className="grid gap-6 lg:grid-cols-3">
        <GlassCard className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center gap-2"><Gift size={18} className="text-[#ec4899]" /><h3 className="font-semibold text-white">Redeem Pink Pin</h3></div>
          {isActive ? (
            <div className="rounded-xl glass-2 p-4 text-sm text-[var(--text-dim)]" data-testid="pinkpin-already-active">
              Your ID <b className="text-white">{user?.member_id}</b> is already active. A Pink Pin can only be used to activate an inactive ID.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="pinkpin-activate-form">
              <div>
                <label className="mb-1 block text-xs text-[var(--text-dim)]">Pink Pin Code</label>
                <input className={inp} placeholder="PP-XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value)} data-testid="pinkpin-code-input" />
              </div>
              <p className="rounded-lg glass-2 p-3 text-[11px] text-[var(--text-dim)]">A Pink Pin gives you a fixed <b className="text-[#ec4899]">5X earning cap</b> on its denomination and Global Pool eligibility. No deposit and no on-chain transaction are involved.</p>
              <LuxuryButton type="submit" variant="gold" disabled={busy} data-testid="pinkpin-activate-submit">
                <Gift size={15} className="mr-1 inline" /> {busy ? "Activating…" : "Activate My ID"}
              </LuxuryButton>
            </form>
          )}
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center gap-2 text-[var(--text-dim)]"><ShieldCheck size={16} className="text-[#ec4899]" /> How it works</div>
          <ul className="mt-3 space-y-2 text-xs text-[var(--text-dim)]">
            <li>• Free activation — no wallet deposit needed.</li>
            <li>• Denominations: $100 / $200 / $500 / $1000.</li>
            <li>• Earn up to <b className="text-white">5X</b> your denomination.</li>
            <li>• Eligible for Global Pool per standard rules.</li>
            <li>• Your ID stays visible in your sponsor's genealogy.</li>
          </ul>
        </GlassCard>
      </div>

      <Dialog open={!!result} onOpenChange={(o) => { if (!o) { setResult(null); window.location.assign("/app"); } }}>
        <DialogContent className="border-[#ec4899]/40 bg-[var(--bg-2)] text-center text-white" data-testid="pinkpin-success-modal">
          <DialogHeader><DialogTitle className="text-center">Pink Pin Activated</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="grid h-16 w-16 place-items-center rounded-full" style={{ background: "linear-gradient(160deg,#f472b6,#ec4899)" }}><CheckCircle2 size={30} className="text-white" /></div>
            <p className="text-lg font-bold" style={{ color: "#ec4899" }}>Your account is now active!</p>
            {result && (
              <div className="w-full rounded-xl p-4 text-left text-sm" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                {[["Denomination", `$${money(result.denomination, 0)}`], ["Earning Cap (5X)", `$${money(result.cap, 0)}`], ["Type", "Pink Pin (Free)"], ["Status", "Active"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-1.5 last:border-0"><span className="text-white/60">{k}</span><span className="font-semibold text-white">{v}</span></div>
                ))}
              </div>
            )}
            <LuxuryButton variant="gold" className="mt-1 w-full" onClick={() => { setResult(null); window.location.assign("/app"); }} data-testid="pinkpin-success-close">GO TO DASHBOARD</LuxuryButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
